import Joi from 'joi';
import i18next, { TFunction } from 'i18next';

/**
 * Creates a customized Joi instance with translated validation messages
 *
 * @param t - Translation function from useTranslation hook
 * @param translationPrefix - Base translation key prefix (e.g., 'EditZuFiConfigModal.validation')
 * @returns Customized Joi instance with translated messages
 *
 * @example
 * const { t } = useTranslation();
 * const CustomJoi = createJoiWithTranslations(t, 'YourModal.validation');
 *
 * const schema = CustomJoi.object({
 *   name: CustomJoi.string().required(),
 *   email: CustomJoi.string().email().required(),
 *   age: CustomJoi.number().min(18).required()
 * });
 */
export const createJoiWithTranslations = (
  t: TFunction,
  translationPrefix: string = 'common.validation'
) => {
  return Joi.defaults((schema) => {
    switch (schema.type) {
      case 'string':
        return schema.messages({
          'string.empty': t(`${translationPrefix}.string.empty`),
          'string.min': t(`${translationPrefix}.string.min`),
          'string.max': t(`${translationPrefix}.string.max`),
          'string.email': t(`${translationPrefix}.string.email`),
          'string.pattern.base': t(`${translationPrefix}.string.pattern`),
          'any.required': t(`${translationPrefix}.required`)
        });

      case 'number':
        return schema.messages({
          'number.base': t(`${translationPrefix}.number.base`),
          'number.min': t(`${translationPrefix}.number.min`),
          'number.max': t(`${translationPrefix}.number.max`),
          'number.integer': t(`${translationPrefix}.number.integer`),
          'any.required': t(`${translationPrefix}.required`)
        });

      case 'boolean':
        return schema.messages({
          'boolean.base': t(`${translationPrefix}.boolean.base`),
          'any.required': t(`${translationPrefix}.required`)
        });

      case 'array':
        return schema.messages({
          'array.base': t(`${translationPrefix}.array.base`),
          'array.min': t(`${translationPrefix}.array.min`),
          'array.max': t(`${translationPrefix}.array.max`),
          'any.required': t(`${translationPrefix}.required`)
        });

      case 'object':
        return schema.messages({
          'object.base': t(`${translationPrefix}.object.base`),
          'any.required': t(`${translationPrefix}.required`)
        });

      case 'date':
        return schema.messages({
          'date.base': t(`${translationPrefix}.date.base`),
          'date.min': t(`${translationPrefix}.date.min`),
          'date.max': t(`${translationPrefix}.date.max`),
          'any.required': t(`${translationPrefix}.required`)
        });

      default:
        return schema.messages({
          'any.required': t(`${translationPrefix}.required`)
        });
    }
  });
};

/**
 * Creates a customized Joi instance with field-specific translated validation messages
 * This allows you to have different translations per field
 *
 * @param t - Translation function from useTranslation hook
 * @param modalPrefix - Modal translation key prefix (e.g., 'EditZuFiConfigModal')
 * @returns Customized Joi instance with field-specific translated messages
 *
 * @example
 * const { t } = useTranslation();
 * const CustomJoi = createJoiWithFieldTranslations(t, 'EditZuFiConfigModal');
 *
 * const schema = CustomJoi.object({
 *   gebiet: CustomJoi.string().required().label('gebiet'),
 *   email: CustomJoi.string().email().required().label('email')
 * });
 *
 * // Translation keys:
 * // EditZuFiConfigModal.validation.gebiet.required
 * // EditZuFiConfigModal.validation.email.required
 */
export const createJoiWithFieldTranslations = (
  t: TFunction,
  modalPrefix: string
) => {
  return Joi.defaults((schema) => {
    const label = schema.$_terms.label || 'field';
    const fieldPrefix = `${modalPrefix}.validation.${label}`;

    switch (schema.type) {
      case 'string':
        return schema.messages({
          'string.empty': t(`${fieldPrefix}.required`),
          'string.min': t(`${fieldPrefix}.min`),
          'string.max': t(`${fieldPrefix}.max`),
          'string.email': t(`${fieldPrefix}.email`),
          'string.pattern.base': t(`${fieldPrefix}.pattern`),
          'any.required': t(`${fieldPrefix}.required`)
        });

      case 'number':
        return schema.messages({
          'number.base': t(`${fieldPrefix}.invalid`),
          'number.min': t(`${fieldPrefix}.min`),
          'number.max': t(`${fieldPrefix}.max`),
          'number.integer': t(`${fieldPrefix}.integer`),
          'any.required': t(`${fieldPrefix}.required`)
        });

      case 'boolean':
        return schema.messages({
          'boolean.base': t(`${fieldPrefix}.invalid`),
          'any.required': t(`${fieldPrefix}.required`)
        });

      case 'array':
        return schema.messages({
          'array.base': t(`${fieldPrefix}.invalid`),
          'array.min': t(`${fieldPrefix}.min`),
          'array.max': t(`${fieldPrefix}.max`),
          'any.required': t(`${fieldPrefix}.required`)
        });

      default:
        return schema.messages({
          'any.required': t(`${fieldPrefix}.required`)
        });
    }
  });
};
